export type BookingTab = 'bookings' | 'reservations';

/** The two halves of this page. Reservations are a separate tab rather than
 *  rows in the bookings list because they have no customer record and no
 *  confirmation code until they are paid, and the list sorts and pages in the
 *  database around both. */
export function Tabs({
  active,
  onChange,
  bookingCount,
  reservationCount,
}: {
  active: BookingTab;
  onChange: (t: BookingTab) => void;
  bookingCount: number;
  reservationCount: number;
}) {
  const tab = (id: BookingTab, label: string, count: number) => {
    const isActive = active === id;
    return (
      <button
        key={id}
        type="button"
        role="tab"
        aria-selected={isActive}
        onClick={() => onChange(id)}
        className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-semibold transition ${
          isActive
            ? 'border-moss-600 text-moss-900 dark:border-moss-400 dark:text-moss-300'
            : 'border-transparent text-stone-500 hover:text-stone-700 dark:hover:text-stone-300'
        }`}
      >
        {label}
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${
            // The reservations count is an ATTENTION signal — unpaid seats
            // sitting on inventory — so it is tinted rather than neutral.
            id === 'reservations' && count > 0
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300'
          }`}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div role="tablist" className="mb-5 flex gap-6 border-b border-stone-200 dark:border-stone-700">
      {tab('bookings', 'Bookings', bookingCount)}
      {tab('reservations', 'Reservations', reservationCount)}
    </div>
  );
}
